import React, { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { presetToRange } from "src/features/analytics/constants"
import ReportFilters from "src/features/analytics/components/ReportFilters"
import EmptyReportState from "src/features/analytics/components/EmptyReportState"
import { useChannelPartnerPerformance } from "src/features/channel_partners/hooks/useChannelPartners"

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0)
const pct = (n) => `${Math.round((n || 0) * 100)}%`

export default function ChannelPartnerReportPage() {
  const [preset, setPreset] = useState("30d")
  const [filters, setFilters] = useState(presetToRange("30d"))
  const { data, loading, fetch } = useChannelPartnerPerformance()

  useEffect(() => {
    fetch({ from: filters.from, to: filters.to, project_id: filters.project_id })
  }, [filters.from, filters.to, filters.project_id])

  const rows = data?.rows ?? []
  const totals = data?.totals ?? {}

  return (
    <div className="mx-auto max-w-[1120px]">
      <div className="mb-4">
        <h2 className="text-[17px] font-bold text-slate-900">Channel Partner Performance</h2>
        <p className="text-[13px] text-slate-500">Leads, closings, and commission by partner.</p>
      </div>

      <ReportFilters filters={filters} onFiltersChange={setFilters} preset={preset} onPresetChange={setPreset} />

      {loading ? (
        <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <EmptyReportState message="No channel partner activity in this period." />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-black">Partner</TableHead>
                <TableHead className="text-right font-semibold text-black">Leads</TableHead>
                <TableHead className="text-right font-semibold text-black">Site visits</TableHead>
                <TableHead className="text-right font-semibold text-black">Closings</TableHead>
                <TableHead className="text-right font-semibold text-black">Conv.</TableHead>
                <TableHead className="text-right font-semibold text-black">GMV</TableHead>
                <TableHead className="text-right font-semibold text-black">Commission</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.channel_partner.id}>
                  <TableCell className="text-[13px] font-medium text-slate-900">{r.channel_partner.name}</TableCell>
                  <TableCell className="text-right text-[13px]">{r.leads}</TableCell>
                  <TableCell className="text-right text-[13px]">{r.site_visits}</TableCell>
                  <TableCell className="text-right text-[13px]">{r.closings}</TableCell>
                  <TableCell className="text-right text-[13px]">{pct(r.conversion)}</TableCell>
                  <TableCell className="text-right text-[13px]">{inr(r.gmv)}</TableCell>
                  <TableCell className="text-right text-[13px]">{inr(r.commission)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-slate-50 font-semibold">
                <TableCell className="text-[13px]">Total</TableCell>
                <TableCell className="text-right text-[13px]">{totals.leads}</TableCell>
                <TableCell className="text-right text-[13px]">{totals.site_visits}</TableCell>
                <TableCell className="text-right text-[13px]">{totals.closings}</TableCell>
                <TableCell className="text-right text-[13px]">{pct(totals.conversion)}</TableCell>
                <TableCell className="text-right text-[13px]">{inr(totals.gmv)}</TableCell>
                <TableCell className="text-right text-[13px]">{inr(totals.commission)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
